// src/views/admin/ADMatieres.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const EMPTY_MATIERE = { nom: "", code: "", description: "", coefficient: 1, annee_scolaire: "" };
const EMPTY_ENS = { classe: "", matiere: "", enseignant: "", annee_scolaire: "" };

export default function ADMatieres({ slug }) {
  const [activeTab, setActiveTab] = useState("matieres");

  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [classes, setClasses] = useState([]);
  const [classeFilt, setClasseFilt] = useState("");
  const [enseignants, setEnseignants] = useState([]);

  const [matieres, setMatieres] = useState([]);
  const [enseignements, setEnseignements] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [formMat, setFormMat] = useState(EMPTY_MATIERE);
  const [formEns, setFormEns] = useState(EMPTY_ENS);
  const [pickedClasseIds, setPickedClasseIds] = useState([]);

  const toast = useToastHook();
  const {
    getAnneesScolaires, getClasses, getUtilisateurs,
    getMatieres, createMatiere, updateMatiere, deleteMatiere, assignMatiereClasses,
    getEnseignements, createEnseignement, updateEnseignement, deleteEnseignement,
    genererMatieresStandard,
  } = useAdminService();

  useEffect(() => {
    if (slug) init();
  }, [slug]);

  useEffect(() => {
    if (slug && anneeFilt) loadData();
  }, [slug, anneeFilt]);

  // Le filtre classe dépend de la liste des classes de l'année : on le
  // réinitialise si la classe sélectionnée n'existe plus après un
  // changement d'année, pour éviter un filtre "fantôme".
  useEffect(() => {
    if (classeFilt && !classes.some(c => String(c.id) === String(classeFilt))) {
      setClasseFilt("");
    }
  }, [classes]); // eslint-disable-line react-hooks/exhaustive-deps

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, classesData, ensData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getClasses(slug, { show_archived: "false" }),
        getUtilisateurs(slug, { role: "ENSEIGNANT", page_size: "all" }),
      ]);
      setAnnees(anneesData);
      setClasses(classesData);
      setEnseignants(ensData.results || ensData);

      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [matData, ensData] = await Promise.all([
        getMatieres(slug, { annee_scolaire: anneeFilt }),
        getEnseignements(slug, { annee_scolaire: anneeFilt }),
      ]);
      setMatieres(matData);
      setEnseignements(ensData);
    } catch (error) {
      toast.error("Impossible de charger les matières/enseignements");
    }
  };

  const filteredMatieres = matieres.filter(m => {
    const matchSearch = m.nom?.toLowerCase().includes(search.toLowerCase());
    const matchClasse = !classeFilt || (m.classes || []).some(c => String(c.id) === String(classeFilt));
    return matchSearch && matchClasse;
  });
  const filteredEns = enseignements.filter(e => {
    const matchSearch =
      e.matiere_nom?.toLowerCase().includes(search.toLowerCase()) ||
      e.classe_nom?.toLowerCase().includes(search.toLowerCase()) ||
      e.enseignant_nom?.toLowerCase().includes(search.toLowerCase());
    const matchClasse = !classeFilt || String(e.classe) === String(classeFilt);
    return matchSearch && matchClasse;
  });

  const classeFiltNom = classes.find(c => String(c.id) === String(classeFilt))?.nom;

  // ── Matières ──────────────────────────────────────────────────────────
  const openCreateMat = () => {
    setFormMat({ ...EMPTY_MATIERE, annee_scolaire: anneeFilt });
    setModal("create-mat");
  };
  const openEditMat = (m) => {
    setSelected(m);
    setFormMat({ nom: m.nom, code: m.code || "", description: m.description || "", coefficient: m.coefficient, annee_scolaire: m.annee_scolaire });
    setModal("edit-mat");
  };
  const openDeleteMat = (m) => { setSelected(m); setModal("delete-mat"); };
  const openAssignClasses = (m) => {
    setSelected(m);
    setPickedClasseIds((m.classes || []).map(c => c.id));
    setModal("assign-classes");
  };

  const handleSaveMat = async () => {
    if (!formMat.nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const dataToSend = {
        nom: formMat.nom.trim(),
        code: formMat.code || "",
        description: formMat.description || "",
        coefficient: parseFloat(formMat.coefficient) || 1,
        annee_scolaire: formMat.annee_scolaire || anneeFilt,
      };
      if (modal === "create-mat") {
        const created = await createMatiere(slug, dataToSend);
        setMatieres(prev => [...prev, created]);
        toast.success(`Matière "${created.nom}" créée avec succès`);
      } else {
        const updated = await updateMatiere(slug, selected.id, dataToSend);
        setMatieres(prev => prev.map(m => m.id === selected.id ? updated : m));
        toast.success(`Matière "${updated.nom}" modifiée avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.nom?.[0] || data?.annee_scolaire || "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMat = async () => {
    setSaving(true);
    try {
      await deleteMatiere(slug, selected.id);
      setMatieres(prev => prev.filter(m => m.id !== selected.id));
      toast.success(`Matière "${selected.nom}" supprimée avec succès`);
      setModal(null);
      loadData();
    } catch (error) {
      toast.error("Impossible de supprimer cette matière");
    } finally {
      setSaving(false);
    }
  };

  const toggleClasse = (id) => {
    setPickedClasseIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAssignClasses = async () => {
    setSaving(true);
    try {
      await assignMatiereClasses(slug, selected.id, pickedClasseIds);
      toast.success(`Classes mises à jour pour "${selected.nom}"`);
      setModal(null);
      loadData();
    } catch (error) {
      toast.error("Impossible de mettre à jour les classes");
    } finally {
      setSaving(false);
    }
  };

  /* ── Génération des matières standards ─────────────────────────────────
   * N'apparaît que lorsqu'aucune matière n'existe encore pour l'année
   * sélectionnée et que des classes existent déjà (le backend refuse la
   * génération sans classes). Idempotent côté backend : la rappeler après
   * avoir ajouté de nouvelles classes rattache juste les matières
   * existantes aux nouvelles classes, sans dupliquer.
   * ======================================================================== */
  const handleGenererMatieresStandard = async () => {
    if (!anneeFilt) return;
    setGenerating(true);
    try {
      const res = await genererMatieresStandard(slug, anneeFilt);
      if (res.matieres_creees > 0) {
        toast.success(`${res.matieres_creees} matière(s) générée(s) avec succès`);
        loadData();
      } else {
        toast.info("Aucune matière créée (déjà générées ?)");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de générer les matières standards. Générez d'abord vos classes.");
    } finally {
      setGenerating(false);
    }
  };

  // ── Enseignements ────────────────────────────────────────────────────
  const openCreateEns = () => {
    setFormEns({ ...EMPTY_ENS, classe: classeFilt || "", annee_scolaire: anneeFilt });
    setModal("create-ens");
  };
  const openEditEns = (e) => {
    setSelected(e);
    setFormEns({ classe: e.classe, matiere: e.matiere, enseignant: e.enseignant, annee_scolaire: e.annee_scolaire });
    setModal("edit-ens");
  };
  const openDeleteEns = (e) => { setSelected(e); setModal("delete-ens"); };

  const handleSaveEns = async () => {
    if (!formEns.classe || !formEns.matiere || !formEns.enseignant) {
      toast.error("Classe, matière et enseignant sont requis");
      return;
    }
    setSaving(true);
    try {
      const dataToSend = { ...formEns, annee_scolaire: formEns.annee_scolaire || anneeFilt };
      if (modal === "create-ens") {
        const created = await createEnseignement(slug, dataToSend);
        setEnseignements(prev => [...prev, created]);
        toast.success("Enseignement créé avec succès");
      } else {
        const updated = await updateEnseignement(slug, selected.id, dataToSend);
        setEnseignements(prev => prev.map(x => x.id === selected.id ? updated : x));
        toast.success("Enseignement modifié avec succès");
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === "object" ? Object.values(data).flat().join("\n") : "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEns = async () => {
    setSaving(true);
    try {
      await deleteEnseignement(slug, selected.id);
      setEnseignements(prev => prev.filter(x => x.id !== selected.id));
      toast.success("Enseignement supprimé avec succès");
      setModal(null);
    } catch (error) {
      toast.error("Impossible de supprimer cet enseignement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      {/* Onglets comme dans Configuration */}
      <div className="tab-nav">
        <button
          className={`tab-btn ${activeTab === "matieres" ? "active" : ""}`}
          onClick={() => setActiveTab("matieres")}
        >
          <i className="ti ti-book" /> MATIÈRES
        </button>
        <button
          className={`tab-btn ${activeTab === "enseignements" ? "active" : ""}`}
          onClick={() => setActiveTab("enseignements")}
        >
          <i className="ti ti-chalkboard" /> ENSEIGNEMENTS
        </button>
      </div>


      {/* Barre d'outils */}
      <div className="sb2">
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <select value={classeFilt} onChange={e => setClasseFilt(e.target.value)}>
          <option value="">Toutes les classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <input type="text" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 200 }} />
        <button className="bsec" onClick={loadData}><i className="ti ti-refresh" />Actualiser</button>
        {activeTab === "matieres" && matieres.length === 0 && classes.length > 0 && (
          <button className="bsec" onClick={handleGenererMatieresStandard} disabled={generating}>
            {generating ? (
              <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Génération...</>
            ) : (
              <><i className="ti ti-list-numbers" />Générer les matières standards</>
            )}
          </button>
        )}
        {activeTab === "matieres"
          ? <button className="bprim" onClick={openCreateMat}><i className="ti ti-plus" />Nouvelle matière</button>
          : <button className="bprim" onClick={openCreateEns}><i className="ti ti-plus" />Nouvel enseignement</button>}
      </div>

      {activeTab === "matieres" && classes.length === 0 && (
        <div className="alert-banner">
          <i className="ti ti-alert-triangle" />
          <span>Aucune classe n'existe pour l'année sélectionnée. Créez vos classes avant de générer les matières standards.</span>
        </div>
      )}

      {classeFilt && (
        <div className="filt-hint">
          <i className="ti ti-filter" />
          {activeTab === "matieres"
            ? <span>Matières assignées à la classe <strong>{classeFiltNom}</strong> — {filteredMatieres.length} résultat{filteredMatieres.length > 1 ? "s" : ""}</span>
            : <span>Enseignements de la classe <strong>{classeFiltNom}</strong> — {filteredEns.length} résultat{filteredEns.length > 1 ? "s" : ""}</span>}
          <button type="button" className="filt-clear" onClick={() => setClasseFilt("")}>
            <i className="ti ti-x" />Réinitialiser
          </button>
        </div>
      )}

      {/* ─── TAB MATIÈRES ─── */}
      {activeTab === "matieres" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Matière</th>
                <th>Code</th>
                <th>Coefficient</th>
                <th>Classes assignées</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatieres.map(m => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 500 }}>{m.nom}</td>
                  <td style={{ color: "var(--m)", fontSize: 13 }}>{m.code || "—"}</td>
                  <td>{m.coefficient}</td>
                  <td style={{ fontSize: 13, color: "var(--m)" }}>
                    {(m.classes || []).length === 0 ? "Aucune" : m.classes.map(c => c.nom).join(", ")}
                  </td>
                  <td>
                    <div className="act">
                      {/* Assigner - Vert */}
                      <button
                        title="Assigner aux classes"
                        onClick={() => openAssignClasses(m)}
                        style={{ background: "#28a745", color: "white" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-link" />
                      </button>
                      {/* Modifier - Jaune */}
                      <button
                        title="Modifier"
                        onClick={() => openEditMat(m)}
                        style={{ background: "#ffc107", color: "black" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                      {/* Supprimer - Rouge */}
                      <button
                        className="del"
                        title="Supprimer"
                        onClick={() => openDeleteMat(m)}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredMatieres.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                    {classeFilt ? "Aucune matière assignée à cette classe" : "Aucune matière trouvée"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── TAB ENSEIGNEMENTS ─── */}
      {activeTab === "enseignements" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Matière</th>
                <th>Classe</th>
                <th>Enseignant</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEns.map(e => (
                <tr key={e.id}>
                  <td style={{ fontWeight: 500 }}>{e.matiere_nom}</td>
                  <td>{e.classe_nom}</td>
                  <td style={{ color: "var(--m)", fontSize: 13 }}>{e.enseignant_nom}</td>
                  <td>
                    <div className="act">
                      {/* Modifier - Jaune */}
                      <button
                        title="Modifier"
                        onClick={() => openEditEns(e)}
                        style={{ background: "#ffc107", color: "black" }}
                        onMouseEnter={ev => { ev.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={ev => { ev.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                      {/* Supprimer - Rouge */}
                      <button
                        className="del"
                        title="Supprimer"
                        onClick={() => openDeleteEns(e)}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredEns.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                    {classeFilt ? "Aucun enseignement pour cette classe" : "Aucun enseignement trouvé"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── MODALS ─── */}

      {/* Modal Matière */}
      {(modal === "create-mat" || modal === "edit-mat") && (
        <Modal title={modal === "create-mat" ? "Nouvelle matière" : "Modifier la matière"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg"><label>Nom *</label><input value={formMat.nom} onChange={e => setFormMat(p => ({ ...p, nom: e.target.value }))} /></div>
            <div className="fgrid">
              <div className="fg"><label>Code</label><input value={formMat.code} onChange={e => setFormMat(p => ({ ...p, code: e.target.value }))} /></div>
              <div className="fg"><label>Coefficient</label><input type="number" step="0.5" min="0" value={formMat.coefficient} onChange={e => setFormMat(p => ({ ...p, coefficient: e.target.value }))} /></div>
            </div>
            <div className="fg"><label>Description</label><input value={formMat.description} onChange={e => setFormMat(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="fg">
              <label>Année scolaire</label>
              <select value={formMat.annee_scolaire} onChange={e => setFormMat(p => ({ ...p, annee_scolaire: e.target.value }))}>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveMat} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create-mat" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Supprimer Matière */}
      {modal === "delete-mat" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer <strong>{selected.nom}</strong> ?
            <div style={{ color: "var(--rd)", marginTop: 8 }}>⚠️ Les notes déjà saisies pour cette matière seront supprimées aussi.</div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteMat} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Assigner Classes */}
      {modal === "assign-classes" && selected && (
        <Modal title={`Classes — ${selected.nom}`} onClose={() => setModal(null)}>
          <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {classes.map(c => (
              <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={pickedClasseIds.includes(c.id)} onChange={() => toggleClasse(c.id)} />
                {c.nom}
              </label>
            ))}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleAssignClasses} disabled={saving}>
              {saving ? "..." : "Enregistrer"}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Enseignement */}
      {(modal === "create-ens" || modal === "edit-ens") && (
        <Modal title={modal === "create-ens" ? "Nouvel enseignement" : "Modifier l'enseignement"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Classe *</label>
              <select value={formEns.classe} onChange={e => setFormEns(p => ({ ...p, classe: e.target.value }))}>
                <option value="">-- Sélectionner --</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Matière *</label>
              <select value={formEns.matiere} onChange={e => setFormEns(p => ({ ...p, matiere: e.target.value }))}>
                <option value="">-- Sélectionner --</option>
                {matieres.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Enseignant *</label>
              <select value={formEns.enseignant} onChange={e => setFormEns(p => ({ ...p, enseignant: e.target.value }))}>
                <option value="">-- Sélectionner --</option>
                {enseignants.map(en => <option key={en.id} value={en.id}>{en.first_name} {en.last_name}</option>)}
              </select>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveEns} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create-ens" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Supprimer Enseignement */}
      {modal === "delete-ens" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer l'enseignement <strong>{selected.matiere_nom}</strong> en <strong>{selected.classe_nom}</strong> ?
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteEns} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        /* ─── ONGLETS (thème bleu + or) ─── */
        .tab-nav {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
          border-radius: 10px;
          border-bottom: 3px solid var(--or);
          padding: 8px 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: rgba(255,255,255,0.75);
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .tab-btn i { font-size: 18px; }

        .tab-btn:hover {
          background: rgba(255,255,255,0.12);
          color: var(--or-lt);
        }

        .tab-btn.active {
          background: white;
          color: var(--pr);
          box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
        }
        .tab-btn.active i { color: var(--or); }
        .tab-btn.active:hover { background: white; color: var(--pr); }

        .fgrid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 9px;
        }

        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
        }

        .act button:hover {
          opacity: 0.8 !important;
        }

        .act button.del {
          background: var(--rd);
          color: white;
        }

        .act button.del:hover {
          background: var(--rd-dk);
        }

        .filt-hint {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          font-size: 13px;
          color: var(--m);
          background: var(--s2);
          border: 1px solid var(--b);
          border-radius: 8px;
          padding: 8px 12px;
          margin-bottom: 12px;
        }
        .filt-hint i.ti-filter { color: var(--pr); font-size: 15px; }
        .filt-hint strong { color: var(--t); }
        .filt-clear {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 4px;
          background: none;
          border: none;
          font-family: inherit;
          font-size: 12px;
          font-weight: 600;
          color: var(--m);
          cursor: pointer;
          padding: 2px 4px;
        }
        .filt-clear:hover { color: var(--t); }
        .filt-clear i { font-size: 13px; }

        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
          .fgrid { grid-template-columns: 1fr; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}